// 转换为树的结构
import { parseDatFile } from "./datParser";
import { enhanceTreeDataWithStyling } from "./enhanceTreeDataWithStyling";

// 生成原始树形数据（originTreeData）- 保持原始section格式
const generateOriginTreeData = (sections) => {
    // 统计每个section名称的出现次数，并记录第一次出现的位置
    const sectionFirstSeen = {};
    const sectionCount = {};

    sections.forEach((section, index) => {
        if (sectionFirstSeen[section.name] === undefined) {
            sectionFirstSeen[section.name] = index;
            sectionCount[section.name] = 1;
        } else {
            sectionCount[section.name] = sectionCount[section.name] + 1;
        }
    });

    return sections.map((section, sectionIndex) => {
        // 只有当这不是第一次出现时，才标记为重复
        const isDuplicate = sectionFirstSeen[section.name] !== sectionIndex;

        const children = Object.entries(section.data).map(([key, value], propIndex) => ({
            title: `${key}=${value}`,
            key: `${sectionIndex}-${propIndex}`, // sectionIndex-propIndex
            isLeaf: true,
            isDuplicate: isDuplicate, // 子节点也标记
        }));

        // title 保持原样，不添加任何标记
        const sectionTitle = section.name;

        return {
            title: sectionTitle,
            key: `${sectionIndex}`, // 顶层只用sectionIndex
            children: children,
            isDuplicate: isDuplicate, // 只在第二次及以后出现时为true
            originalSection: section.name
        };
    });
};

// 生成解析后的树形数据（parsedTreeData）- 拆分成层级结构
export const generateParsedTreeData = (sections) => {
    // 构建嵌套对象结构
    const root = {};
    const seenSections = new Set(); // 记录已处理的 section 名称

    sections.forEach(section => {
        // 关键修复：跳过已处理过的重复 section
        if (seenSections.has(section.name)) return;
        seenSections.add(section.name);
        const parts = section.name.split(':');
        let current = root;
        
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (i === parts.length - 1) {
                // 仅当不存在时才设置数据（避免覆盖）
                if (current[part] === undefined) {
                    current[part] = { ...section.data };
                }
            } else {
                if (!current[part] || typeof current[part] !== 'object' || Array.isArray(current[part])) {
                    current[part] = {};
                }
                current = current[part];
            }
        }
    });

    // 固定顺序的前缀
    const fixedOrder = ['AUTOHEAT', 'FILE', 'MODINFO'];

    // 转换为Tree组件需要的格式，使用严格的层级数字key
    const convertToTreeData = (obj, parentKey = '', depth = 0) => {
        if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
            return [];
        }

        const entries = Object.entries(obj);

        // 如果是根层级，应用特殊排序
        let sortedEntries;
        if (depth === 0) {
            const fixedItems = [];
            const otherItems = [];

            entries.forEach(([key, value]) => {
                if (fixedOrder.includes(key)) {
                    fixedItems.push([key, value]);
                } else {
                    otherItems.push([key, value]);
                }
            });

            // 固定项按指定顺序排列
            const sortedFixedItems = fixedOrder
                .map(prefix => fixedItems.find(([key]) => key === prefix))
                .filter(Boolean);

            // 其他项按字母数字排序
            const sortedOtherItems = otherItems.sort(([a], [b]) => {
                return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
            });

            sortedEntries = [...sortedFixedItems, ...sortedOtherItems];
        } else {
            // 非根层级，按字母数字排序
            sortedEntries = entries.sort(([a], [b]) => {
                return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
            });
        }

        return sortedEntries.map(([key, value], itemIndex) => {
            // 生成严格的层级key
            const currentKey = parentKey === ''
                ? `${itemIndex}`
                : `${parentKey}-${itemIndex}`;

            if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0) {
                // 检查是否是叶子节点（只有字符串值）
                const isLeafNode = Object.values(value).every(v => typeof v === 'string');
                if (isLeafNode) {
                    // 叶子节点，属性作为子节点
                    const children = Object.entries(value).map(([propKey, propValue], propIndex) => ({
                        title: `${propKey}=${propValue}`,
                        key: `${currentKey}-${propIndex}`, // currentKey-propIndex
                        isLeaf: true
                    }));
                    return {
                        title: key,
                        key: currentKey,
                        children: children
                    };
                } else {
                    // 非叶子节点，继续递归
                    const children = convertToTreeData(value, currentKey, depth + 1);
                    return {
                        title: key,
                        key: currentKey,
                        children: children
                    };
                }
            } else {
                // 单个值的情况（理论上不会出现，但为了安全）
                return {
                    title: `${key}=${value}`,
                    key: currentKey,
                    isLeaf: true
                };
            }
        });
    };

    return convertToTreeData(root);
};

// 主要的解析函数
export const parseDatForTree = (datContent) => {
    const sections = parseDatFile(datContent);
    const originTreeData = generateOriginTreeData(sections);
    // 增强originTreeData，添加样式
    const enhancedOriginTreeData = enhanceTreeDataWithStyling(originTreeData);

    const parsedTreeData = generateParsedTreeData(sections);

    return {
        enhancedOriginTreeData,
        parsedTreeData
    };
};